import React, { useState, useMemo } from 'react';

function ApiDataViewer({ data = [], isLoading = false, lastFetchTime = null, dataSourceType = 'custom' }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Get all unique column headers from the data
    const columns = useMemo(() => {
        if (!data || data.length === 0) return [];
        const allKeys = new Set();
        data.forEach(row => {
            Object.keys(row).forEach(key => allKeys.add(key));
        });
        return Array.from(allKeys);
    }, [data]);

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) return data;
        const term = searchTerm.toLowerCase();
        return data.filter(row =>
            Object.values(row).some(val =>
                String(val).toLowerCase().includes(term)
            )
        );
    }, [data, searchTerm]);

    // Format the last fetch time
    const formattedTime = lastFetchTime
        ? new Date(lastFetchTime).toLocaleTimeString()
        : null;

    // Don't show if no data and not loading
    if ((!data || data.length === 0) && !isLoading) {
        return null;
    }

    return (
        <div className="sidebar-panel api-data-viewer">
            <div
                className="api-data-header"
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    marginBottom: isExpanded ? '12px' : '0',
                }}
            >
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{isExpanded ? '▼' : '▶'}</span>
                    <span>API Data</span>
                    {data.length > 0 && (
                        <span
                            style={{
                                fontSize: '11px',
                                backgroundColor: 'var(--color-primary)',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontWeight: 'normal',
                            }}
                        >
                            {data.length} items
                        </span>
                    )}
                </h3>
                {formattedTime && (
                    <span style={{ fontSize: '10px', color: 'var(--color-text-secondary)' }}>
                        Last: {formattedTime}
                    </span>
                )}
            </div>

            {isExpanded && (
                <div className="api-data-content">
                    {isLoading && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px',
                            gap: '10px',
                            color: 'var(--color-text-secondary)',
                        }}>
                            <div className="loading-spinner-sm" />
                            <span>Fetching data...</span>
                        </div>
                    )}

                    {!isLoading && data.length > 0 && (
                        <>
                            {/* Search Input */}
                            <div style={{ marginBottom: '12px' }}>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="🔍 Search data..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ fontSize: '12px', padding: '8px 12px' }}
                                />
                            </div>

                            {/* Scrollable Table Container */}
                            <div
                                className="data-table-container"
                                style={{
                                    maxHeight: '250px',
                                    overflowY: 'auto',
                                    overflowX: 'auto',
                                    borderRadius: '6px',
                                    border: '1px solid var(--color-border)',
                                }}
                            >
                                <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: 'var(--color-surface-alt)', position: 'sticky', top: 0 }}>
                                            {columns.map((col) => (
                                                <th
                                                    key={col}
                                                    style={{
                                                        padding: '8px 10px',
                                                        textAlign: 'left',
                                                        fontWeight: '600',
                                                        borderBottom: '2px solid var(--color-border)',
                                                        whiteSpace: 'nowrap',
                                                        color: 'var(--color-text)',
                                                    }}
                                                >
                                                    {col}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((row, idx) => (
                                            <tr
                                                key={idx}
                                                className="data-table-row"
                                                style={{
                                                    backgroundColor: idx % 2 === 0 ? 'transparent' : 'var(--color-surface)',
                                                    transition: 'background-color 0.15s',
                                                }}
                                            >
                                                {columns.map((col) => (
                                                    <td
                                                        key={col}
                                                        style={{
                                                            padding: '6px 10px',
                                                            borderBottom: '1px solid var(--color-border)',
                                                            maxWidth: '150px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            color: 'var(--color-text-secondary)',
                                                        }}
                                                        title={String(row[col] ?? '')}
                                                    >
                                                        {row[col] !== undefined ? String(row[col]) : '-'}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Filter Results Info */}
                            {searchTerm && (
                                <div style={{ marginTop: '8px', fontSize: '10px', color: 'var(--color-text-secondary)' }}>
                                    Showing {filteredData.length} of {data.length} rows
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default ApiDataViewer;
